import React from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import {
  LuArrowUpDown,
  LuCalendar,
  LuDatabase,
  LuPencil,
  LuTags,
  LuTrash2,
} from "react-icons/lu";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  setEditMaterial,
  toggleMaterialModal,
} from "../../features/materials/materialsSlice";
import { getLoggingUser } from "../../features/loggingUser/loggingUserSlice";
import useConfirmPopup from "../../context/useConfirmPopup";
import { stockPath, totalStock } from "../../static/materialStock";
import { hasPermission, permission } from "../../static/permission";
import {
  MaterialSortBy,
  MaterialSortOrder,
  MaterialsProps,
} from "../../types/material";
import { TagIcon } from "../icons";
import StockChip from "../materialStocks/StockChip";
import Button from "../ui/Button";
import PhotoCell from "../ui/PhotoCell";
import SortableTableHead from "../ui/SortableTableHead";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/Table";
import {
  EmptyState,
  LoadingState,
  TableEmptyRow,
  TableLoadingRow,
} from "../ui/TableStateRow";
import cx from "../ui/cx";
import {
  cardClass,
  cardContentFlushClass,
  mobileCardClass,
  tableHeadCellClass,
} from "../ui/styles";

const formatDate = (value: string) =>
  moment(value).format("DD-MM-YYYY hh:mm A");

const cardRowClass = "flex items-start justify-between gap-4";

const cardLabelClass = cx(tableHeadCellClass, "shrink-0");

// The phone card's buttons share one row.
const footerGridClass: Record<number, string> = {
  1: "grid-cols-1",
  2: "grid-cols-2",
  3: "grid-cols-3",
  4: "grid-cols-4",
};

// A stock entry needs one of the material's sizes, which the module no longer shows; every material made since has one.
const noStockEntryTitle = "આ મટીરીયલ નો સ્ટોક બદલી શકાશે નહીં";

type Props = {
  materials: MaterialsProps[];
  pageNumber: number;
  pageSize: number;
  isLoading: boolean;
  // The order the materials were loaded in.
  sortBy: MaterialSortBy;
  sortOrder: MaterialSortOrder;
  onSort: (sortBy: MaterialSortBy, sortOrder: MaterialSortOrder) => void;
  // The names of the list's sorts, for the headers' hover text.
  sortLabels: Record<MaterialSortBy, string>;
  handleDeleteMaterial: (id: number) => void;
  // Opens the stock popup (ઉમેરો / બાદ) for the material.
  onStockEntry: (material: MaterialsProps) => void;
};

const MaterialList = ({
  materials,
  pageNumber,
  pageSize,
  isLoading,
  sortBy,
  sortOrder,
  onSort,
  sortLabels,
  handleDeleteMaterial,
  onStockEntry,
}: Props) => {
  const dispatch = useAppDispatch();
  const confirmDelete = useConfirmPopup();
  const loggingUser = useAppSelector(getLoggingUser);

  const canUpdate = hasPermission(
    loggingUser?.permissions,
    permission.materialsUpdate
  );
  const canDelete = hasPermission(
    loggingUser?.permissions,
    permission.materialsDelete
  );
  // The list shows each material's stock, and the row's stock button opens the material's stock page.
  const canStock = hasPermission(
    loggingUser?.permissions,
    permission.materialStocksRead
  );
  const canStockEntry = hasPermission(
    loggingUser?.permissions,
    permission.materialStocksCreate
  );

  const actionCount =
    (canStock ? 1 : 0) +
    (canStockEntry ? 1 : 0) +
    (canUpdate ? 1 : 0) +
    (canDelete ? 1 : 0);
  const tableColSpan = 4 + (canStock ? 1 : 0) + (actionCount > 0 ? 1 : 0);

  // The date header shows both dates, so it also stands for the last-changed date the filter can pick.
  const sortHead = (column: MaterialSortBy, label: string) => (
    <SortableTableHead
      column={column}
      alsoFor={column === "created_at" ? ["updated_at"] : undefined}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSort={onSort}
      sortLabels={sortLabels}
      disabled={isLoading}
    >
      {label}
    </SortableTableHead>
  );

  const handleEditMaterial = async (material: MaterialsProps) => {
    await dispatch(setEditMaterial(material));
    dispatch(toggleMaterialModal(true));
  };

  const handleDeleteRow = async (id: number) => {
    const confirmed = await confirmDelete(
      `તમે આને પાછું ફેરવી શકશો નહીં!`,
      `શું તમે ખાતરી આપો છો?`,
      "હા, તેને કાઢી નાખો!",
      true
    );

    if (confirmed) {
      handleDeleteMaterial(id);
    }
  };

  // The material's bars in stock, shown only to a user who may see the stock.
  const stock = (material: MaterialsProps) => (
    <StockChip stock={totalStock(material.sizes || [])} />
  );

  const actionButtons = (material: MaterialsProps, isMobile: boolean) => {
    // A material without a size can't take a stock entry: its button stays, disabled, and says why on hover.
    const hasSizes = (material.sizes?.length ?? 0) > 0;
    const buttonProps = isMobile
      ? { variant: "outline" as const, className: "w-full" }
      : { variant: "ghost" as const, size: "iconSm" as const };

    return (
      <>
        {canStock && (
          <Link
            to={stockPath(material.id)}
            className={isMobile ? "block" : "inline-flex"}
          >
            <Button {...buttonProps} title="સ્ટોક વિગતો">
              <LuTags className="h-4 w-4" />
            </Button>
          </Link>
        )}

        {canStockEntry && (
          <span
            title={hasSizes ? undefined : noStockEntryTitle}
            className={isMobile ? "block" : "inline-flex"}
          >
            <Button
              {...buttonProps}
              title="સ્ટોક વધ-ઘટ"
              disabled={!hasSizes}
              className={cx(buttonProps.className, "text-[#113F67]")}
              onClick={() => onStockEntry(material)}
            >
              <LuArrowUpDown className="h-4 w-4" />
            </Button>
          </span>
        )}

        {canUpdate && (
          <Button
            {...buttonProps}
            title="સંપાદિત કરો"
            onClick={() => handleEditMaterial(material)}
          >
            <LuPencil className="h-4 w-4" />
          </Button>
        )}

        {canDelete && (
          <Button
            {...buttonProps}
            title="કાઢી નાખો"
            className={cx(buttonProps.className, "text-red-600 hover:text-red-700")}
            onClick={() => handleDeleteRow(material.id)}
          >
            <LuTrash2 className="h-4 w-4" />
          </Button>
        )}
      </>
    );
  };

  return (
    <>
      <div className={cx(cardClass, "max-sm:hidden")}>
        <div className={cardContentFlushClass}>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {sortHead("id", "ક્રમ")}
                  <TableHead className={tableHeadCellClass}>ફોટો</TableHead>
                  {sortHead("name", "મટીરીયલ નામ")}
                  {canStock && (
                    <TableHead className={tableHeadCellClass}>સ્ટોક</TableHead>
                  )}
                  {sortHead("created_at", "તારીખ")}
                  {actionCount > 0 && (
                    <TableHead className={cx(tableHeadCellClass, "text-right")}>
                      એકશન્સ બટન
                    </TableHead>
                  )}
                </TableRow>
              </TableHeader>

              {isLoading ? (
                <TableLoadingRow colSpan={tableColSpan} />
              ) : materials.length === 0 ? (
                <TableEmptyRow
                  colSpan={tableColSpan}
                  label="મટીરીયલ ની વિગતો મળી નથી"
                />
              ) : (
                <TableBody>
                  {materials.map((material, index) => (
                    <TableRow key={material.id}>
                      <TableCell className="font-medium">
                        {(pageNumber - 1) * pageSize + index + 1}
                      </TableCell>

                      <TableCell>
                        <PhotoCell
                          url={material.image_url}
                          alt={material.name}
                        />
                      </TableCell>

                      <TableCell>
                        <div className="flex items-center gap-2">
                          <TagIcon className="h-4 w-4 text-gray-400" />
                          <div>
                            <div className="font-medium">{material.name}</div>
                            <div className="text-sm text-gray-500">
                              ID: {material.id}
                            </div>
                          </div>
                        </div>
                      </TableCell>

                      {canStock && <TableCell>{stock(material)}</TableCell>}

                      <TableCell className="whitespace-nowrap">
                        <div className="flex flex-col gap-1">
                          <span className="flex items-center gap-1">
                            <LuCalendar className="h-3.5 w-3.5 text-blue-800" />
                            {formatDate(material.created_at)}
                          </span>
                          <span className="flex items-center gap-1 text-xs text-gray-400">
                            <LuDatabase className="h-3.5 w-3.5 text-gray-400" />
                            {formatDate(material.updated_at)}
                          </span>
                        </div>
                      </TableCell>

                      {actionCount > 0 && (
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            {actionButtons(material, false)}
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              )}
            </Table>
          </div>
        </div>
      </div>

      <div className="space-y-4 sm:hidden">
        {isLoading ? (
          <div className={cx(mobileCardClass, "p-4 text-sm")}>
            <LoadingState />
          </div>
        ) : materials.length === 0 ? (
          <div className={cx(mobileCardClass, "p-4 text-sm")}>
            <EmptyState label="મટીરીયલ ની વિગતો મળી નથી" />
          </div>
        ) : (
          materials.map((material, index) => (
            <div key={material.id} className={mobileCardClass}>
              <div className="flex items-center gap-2 border-b p-4">
                <TagIcon className="h-4 w-4 flex-shrink-0 text-gray-400" />
                <div className="min-w-0">
                  <div className="font-medium break-words">{material.name}</div>
                  <div className="text-sm text-gray-500">ID: {material.id}</div>
                </div>
              </div>

              <div className="space-y-3 p-4 text-sm">
                <div className={cardRowClass}>
                  <span className={cardLabelClass}>ક્રમ</span>
                  <span className="font-medium">
                    {(pageNumber - 1) * pageSize + index + 1}
                  </span>
                </div>

                <div className={cardRowClass}>
                  <span className={cardLabelClass}>ફોટો</span>
                  <PhotoCell url={material.image_url} alt={material.name} />
                </div>

                {canStock && (
                  <div className={cardRowClass}>
                    <span className={cardLabelClass}>સ્ટોક</span>
                    {stock(material)}
                  </div>
                )}

                <div className={cardRowClass}>
                  <span className={cardLabelClass}>તારીખ</span>
                  <span className="text-right text-gray-500">
                    {formatDate(material.created_at)}
                    <span className="block text-xs text-gray-400">
                      {formatDate(material.updated_at)}
                    </span>
                  </span>
                </div>
              </div>

              {actionCount > 0 && (
                <div
                  className={cx(
                    "grid gap-2 border-t p-4",
                    footerGridClass[actionCount]
                  )}
                >
                  {actionButtons(material, true)}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </>
  );
};
export default MaterialList;
